import { createContext, useContext } from 'react';
import { escape } from 'lodash';
import { DepartmentBreadcrumbItem } from 'components/department/DepartmentBreadcrumb';
import { ISpecialPlaceholder } from 'pages/revisions-module/template-editor/editors/template/extensions/placeholder/Placeholder';
import { HierarchyAvailableLevelsResponse } from 'api/actions/hierarchy-available-levels/hierarchy-available-levels-response';

export const DepartmentPlaceholdersContext = createContext<ISpecialPlaceholder[]>([]);
const LEVEL_PREFIX = '_departmentLevel_';

/** Returns an immutable field name without dots, which are reserved for form paths. */
export function departmentLevelFieldName(slug: string): `_${string}` {
  return `${LEVEL_PREFIX}${slug.replace(/\./g, '_')}`;
}

/** Creates one inline revision property per system hierarchy level. */
export function createDepartmentPlaceholders(hierarchies: HierarchyAvailableLevelsResponse): ISpecialPlaceholder[] {
  return hierarchies.flatMap(({ hierarchyName, levels }) =>
    levels.map(({ slug, label }) => ({
      context: 'revision',
      name: departmentLevelFieldName(slug),
      label: `${label} (${hierarchyName})`,
      type: 'revisionProperty',
    }))
  );
}

/** Shares the loaded catalogue with menus, selectors and document editor labels. */
export function useDepartmentPlaceholders() {
  return useContext(DepartmentPlaceholdersContext);
}

/** Resolves canonical ancestors and clears stale values from a previously associated object. */
export function resolveDepartmentPlaceholderFields(
  breadcrumbs: DepartmentBreadcrumbItem[],
  previousFields: Record<string, unknown>
): Record<string, string> {
  const fields: Record<string, string> = {};
  for (const name of Object.keys(previousFields).filter((name) => name.startsWith(LEVEL_PREFIX))) {
    fields[name] = '';
  }
  fields._departmentLevels = breadcrumbs
    .map(({ levelLabel, departmentName }) => `<b>${escape(levelLabel)}</b>: ${escape(departmentName)}<br>`)
    .join('');
  for (const { hierarchyLevelSlug, departmentName } of breadcrumbs) {
    if (hierarchyLevelSlug) {
      fields[departmentLevelFieldName(hierarchyLevelSlug)] = escape(departmentName);
    }
  }
  return fields;
}
